import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Trash2, X } from "lucide-react";
import { Btn, Section } from "@/components/account/Shell";
import { Cell, Dash, Filters, IconBtn, PillRow, Sheet, Tag, Two, Txt } from "@/components/account/Sheet";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { themeOf } from "@/lib/mall";

const FILTERS = [["pending", "In review"], ["active", "Live"], ["rejected", "Sent back"], ["", "All"]];
const COLS = [
    { key: "image", label: "Photo", w: "72px" },
    { key: "shop", label: "Shop", sub: "who hung it", w: "minmax(160px,1fr)" },
    { key: "headline", label: "Headline", sub: "the big line · the small line", w: "minmax(240px,2fr)" },
    { key: "cta", label: "Button", sub: "label → link", w: "minmax(180px,1.2fr)" },
    { key: "product", label: "Shows", sub: "the product card on it", w: "minmax(140px,1fr)" },
    { key: "status", label: "Status", w: "110px" },
    { key: "actions", label: "Actions", sub: "approve · send back · remove", w: "210px" },
];

export default function BillboardsAdmin() {
    const [status, setStatus] = useState("pending");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = useCallback(() => {
        api.get("/platform/billboards", { params: status ? { status } : {} })
            .then(({ data }) => setData(data)).catch(() => setData({ billboards: [], counts: {} }));
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const review = async (id, decision) => {
        const note = decision === "reject" ? window.prompt("What should the shop fix?") ?? "" : "";
        setBusy(id);
        try {
            await api.post(`/platform/billboards/${id}/review`, { decision, note });
            toast.success(decision === "approve" ? "Sign is up on the homepage" : decision === "reject" ? "Sent back to the shop" : "Sign removed");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(null); }
    };

    return (
        <div data-testid="admin-billboards">
            <Filters testid="admin-billboards-filters">
                <PillRow label="Show" testid="billboards-filter" value={status} onPick={setStatus}
                    choices={FILTERS.map(([key, label]) => ({ key, label, count: data?.counts?.[key] }))} />
            </Filters>
            <div className="mt-3">
                {!data ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#4a4a4a]" /></div> : (
                    <Section title={`${data.billboards.length} billboards`} pad={false} testid="admin-billboards-sheet" note="shops send their signs here from their back office">
                        <Sheet cols={COLS} rows={data.billboards} rowKey={(b) => b.id} testid="billboards-sheet" minWidth={1240}
                            rowTestid={(b) => `admin-billboard-${b.id}`} empty="Nothing here. Shops send their signs to 13thav support from their back end."
                            cells={(b) => {
                                const theme = themeOf(b.store);
                                return {
                                    image: (
                                        <Cell>
                                            <span className="h-8 w-14 shrink-0 overflow-hidden border border-[#d0d0d0] bg-[#fafafa]">
                                                {b.image && <img src={mediaUrl(b.image)} alt="" className="h-full w-full object-cover" />}
                                            </span>
                                        </Cell>
                                    ),
                                    shop: (
                                        <Cell>
                                            <span className="h-3 w-3 shrink-0 border border-black/10" style={{ background: theme.primary }} aria-hidden />
                                            <Two a={b.store?.name} b={b.store?.tier_label} />
                                        </Cell>
                                    ),
                                    headline: <Cell><Two a={b.headline} b={b.subhead} /></Cell>,
                                    cta: <Cell><Two a={b.cta_label || "Open"} b={b.cta_url || `/stores/${b.store_slug}`} /></Cell>,
                                    product: <Cell>{b.product?.name ? <Txt>{b.product.name}</Txt> : <Dash why="No product card on this sign" />}</Cell>,
                                    status: <Cell><Tag status={b.status} testid={`billboard-status-${b.id}`} /></Cell>,
                                    actions: (
                                        <Cell className="justify-end">
                                            {b.status !== "active" && (
                                                <Btn tone="solid" onClick={() => review(b.id, "approve")} disabled={busy === b.id} testid={`billboard-approve-${b.id}`}><Check size={10} /> Approve</Btn>
                                            )}
                                            {b.status !== "rejected" && (
                                                <Btn onClick={() => review(b.id, "reject")} disabled={busy === b.id} testid={`billboard-reject-${b.id}`}><X size={10} /> Send back</Btn>
                                            )}
                                            <IconBtn onClick={() => review(b.id, "remove")} disabled={busy === b.id} testid={`billboard-remove-${b.id}`} title="Take this sign down" danger><Trash2 size={12} /></IconBtn>
                                        </Cell>
                                    ),
                                };
                            }} />
                    </Section>
                )}
            </div>
        </div>
    );
}
